import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import BodyText from "@/components/BodyText";
import RichText from "@/components/RichText";
import { api } from "@/lib/api";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";

const CHECKOUT_BRIDGE_URL = "https://fourlevelsexperience.com/api/checkout/redirect";

// Read a first-party cookie by name; returns "" when absent.
const readCookie = (name) => {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
};

export default function FourLevelsEnroll() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const t = useContent("enrollment");
  useSeo("enrollment", {
    title: "Begin the Four Levels Experience — Quantum Results",
    description:
      "A one-time investment of $27 gives you immediate access and lifetime use.",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email so we know who's joining.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enrollments", {
        name: name.trim(),
        email: email.trim(),
        source: "four-levels",
      });
    } catch (err) {
      // Payment matters more than logging — never block the flow
      console.warn("Enrollment log failed; proceeding to Stripe.", err);
    }
    // Hand the buyer off to the Four Levels attribution bridge, which
    // generates client_reference_id server-side and redirects to the same
    // $27 Stripe Payment Link. Meta Pixel cookies (_fbp, _fbc) are set
    // first-party on quantumresults.com but cannot be read from the
    // fourlevelsexperience.com domain — so we forward them via querystring
    // when available. Absent cookies are simply omitted; the buyer still
    // proceeds normally.
    const fbp = readCookie("_fbp");
    const fbc = readCookie("_fbc");
    const bridge = new URL(CHECKOUT_BRIDGE_URL);
    bridge.searchParams.set("prefilled_email", email.trim());
    if (fbp) bridge.searchParams.set("fbp", fbp);
    if (fbc) bridge.searchParams.set("fbc", fbc);
    window.location.href = bridge.toString();
  };

  return (
    <div data-testid="page-four-levels-enroll">
      <PageHero
        eyebrow={t("hero_eyebrow", "The Four Levels Experience")}
        title={t("hero_title", "Welcome")}
        lede={t(
          "hero_lede",
          "You've already taken the first step. You've recognized a question worth exploring."
        )}
        testId="enroll-hero"
      />

      <Section
        eyebrow={t("awaits_eyebrow", "Here's what awaits you")}
        title={t("awaits_title", "Six guided audio conversations, at your own pace.")}
        testId="enroll-awaits"
        className="-mt-10 sm:-mt-12"
      >
        <BodyText
          value={t.body(
            "awaits_body",
            `**Six guided audio conversations** — about one hour total.

**Your Recognition Guide** — a companion to notice what resonates.

**Lifetime access** — return whenever you'd like.

Move through the experience at your own pace. There is no right way. Only your way.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("before_eyebrow", "Before you begin")}
          title={t("before_title", "Slow down.")}
          testId="enroll-before"
        >
          <BodyText
            value={t.body(
              "before_body",
              `There is nothing to memorize. Nothing to master.

Simply notice what resonates. Pause when you'd like. Write down what stands out.

Some of the most meaningful insights arrive after the conversations are over.`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("personal_eyebrow", "A personal note")}
        title={t("personal_title", "Thank you for allowing me to be part of your journey.")}
        testId="enroll-personal"
      >
        <BodyText
          value={t.body(
            "personal_body",
            "I hope this experience helps you discover something meaningful about yourself."
          )}
        />
        <RichText
          as="div"
          className="mt-6 text-[14px] italic text-brand-ink/55"
          value={t("personal_signature", "— Dan Hoover")}
        />
      </Section>

      <section className="bg-brand-paper/60 border-t border-brand-rule/60" data-testid="enroll-cta-section">
        <div className="container-narrow py-20 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl mx-auto text-center"
          >
            <div aria-hidden className="mx-auto mb-6 h-px w-12 bg-brand-blue/30" />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] mb-6"
              value={t("cta_headline", "Begin the Four Levels Experience")}
            />
            <RichText
              as="p"
              className="prose-qr mb-4"
              value={t(
                "cta_body",
                "A one-time investment of $27 gives you immediate access and lifetime use."
              )}
            />
            <RichText
              as="p"
              className="font-serif italic text-[18px] text-brand-ink/70 mb-10"
              value={t("cta_ready_line", "When you're ready… I'd be honored to guide you.")}
            />

            <form onSubmit={onSubmit} className="text-left space-y-4" data-testid="enroll-form">
              <div>
                <label htmlFor="enroll-name" className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">Your name</label>
                <input
                  id="enroll-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="enroll-name"
                  className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-brand-blue"
                />
              </div>
              <div>
                <label htmlFor="enroll-email" className="block text-[11.5px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">Your email</label>
                <input
                  id="enroll-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="enroll-email"
                  className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-brand-blue"
                />
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enroll-submit"
                  className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-brand-ink px-8 py-4 text-brand-cream text-[15px] tracking-wide hover:bg-brand-blue transition-colors disabled:opacity-60"
                >
                  {submitting ? "Taking you to Stripe…" : t("cta_button_label", "Begin the Four Levels Experience")}
                  <span aria-hidden>→</span>
                </button>
                <p className="mt-3 text-center text-[12px] text-brand-ink/45">
                  Secure payment via Stripe. You'll be redirected to complete your purchase.
                </p>
              </div>
            </form>

            <div className="mt-10">
              <Link
                to="/four-levels"
                data-testid="enroll-back-link"
                className="text-[13px] text-brand-ink/55 hover:text-brand-blue"
              >
                ← Back to Four Levels
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
